/**
 * Правка уже заведённого поставщика.
 *
 * Визитку читает модель, и читает хорошо, но не безошибочно: стенд меняется
 * на второй день выставки, в номере телефона теряется цифра, компанию
 * записали латиницей с опечаткой. До сих пор поправить это было нечем —
 * оставалось завести поставщика заново, и в каталоге появлялся двойник
 * с половиной камней.
 *
 * Пустое поле здесь означает «стереть», а не «не знаю»: человек видит форму
 * целиком и отвечает за то, что в ней осталось. Сервер различает эти два
 * случая — телефон при съёмке визитки по-прежнему шлёт только заполненное.
 */
import { useEffect, useState } from 'react';
import { db, enqueue, type LocalSupplier } from '../db';
import { syncNow } from '../sync';
import { ulid } from '@core';

/** Каналы связи, которые бывают на визитках гемм-рынка. */
const KINDS: Array<{ id: string; cap: string }> = [
  { id: 'phone', cap: 'Телефон' },
  { id: 'whatsapp', cap: 'WhatsApp' },
  { id: 'wechat', cap: 'WeChat' },
  { id: 'email', cap: 'Почта' },
  { id: 'telegram', cap: 'Telegram' },
  { id: 'line', cap: 'Line' },
];

type Contact = { id: string; kind: string; value: string };

export default function SupplierEdit({ supplierId, onBack, onSaved }: {
  supplierId: string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const [supplier, setSupplier] = useState<LocalSupplier | null>(null);
  const [form, setForm] = useState({
    companyName: '', companyNameLocal: '', contactPerson: '', position: '',
    booth: '', hall: '', floor: '', office: '', city: '', country: '', address: '', website: '', notes: '',
  });
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [adding, setAdding] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      const s = await db.suppliers.get(supplierId);
      if (!s) return;
      setSupplier(s);
      setForm({
        companyName: s.companyName ?? '', companyNameLocal: s.companyNameLocal ?? '',
        contactPerson: s.contactPerson ?? '', position: s.position ?? '',
        booth: s.booth ?? '', hall: s.hall ?? '', floor: s.floor ?? '', office: s.office ?? '', city: s.city ?? '',
        country: s.country ?? '', address: s.address ?? '',
        website: s.website ?? '', notes: s.notes ?? '',
      });
      setContacts(s.contacts ?? []);
    })();
  }, [supplierId]);

  const set = (k: keyof typeof form) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const name = form.companyName.trim() || form.companyNameLocal.trim();

  async function save() {
    if (!supplier || !name) return;
    setSaving(true);

    const clean = contacts
      .map((c) => ({ ...c, value: c.value.trim() }))
      .filter((c) => c.value);
    // Опустевший контакт — то же удаление, просто человек стёр текст,
    // а не нажал «убрать».
    const gone = [...new Set([
      ...removed,
      ...contacts.filter((c) => !c.value.trim()).map((c) => c.id),
    ])];

    const next: LocalSupplier = {
      ...supplier,
      companyName: name,
      companyNameLocal: form.companyNameLocal.trim() || undefined,
      contactPerson: form.contactPerson.trim() || undefined,
      position: form.position.trim() || undefined,
      booth: form.booth.trim() || undefined,
      hall: form.hall.trim() || undefined,
      floor: form.floor.trim() || undefined,
      office: form.office.trim() || undefined,
      city: form.city.trim() || undefined,
      country: form.country.trim() || undefined,
      address: form.address.trim() || undefined,
      website: form.website.trim() || undefined,
      notes: form.notes.trim() || undefined,
      contacts: clean,
    };
    await db.suppliers.put(next);

    /*
     * Серверу поля уходят строками, а не undefined: пустая строка — это
     * «стереть». Через undefined очистка не проходит вовсе, поле остаётся
     * с прежним значением, и правка молча не срабатывает.
     */
    await enqueue('supplier.upsert', {
      id: supplier.id,
      exhibitionId: supplier.exhibitionId,
      code: supplier.code,
      companyName: name,
      companyNameLocal: form.companyNameLocal.trim(),
      contactPerson: form.contactPerson.trim(),
      position: form.position.trim(),
      booth: form.booth.trim(),
      hall: form.hall.trim(),
      floor: form.floor.trim(),
      office: form.office.trim(),
      city: form.city.trim(),
      country: form.country.trim(),
      address: form.address.trim(),
      website: form.website.trim(),
      notes: form.notes.trim(),
      contacts: clean,
      contactsRemoved: gone,
    }, ulid());

    void syncNow();
    setSaving(false);
    onSaved();
  }

  if (!supplier) return <div className="app"><div className="blankslate">Загружаю…</div></div>;

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Назад</button>
        <h1 className="t">Правка стенда</h1>
        <p className="meta">Пустое поле — стереть. Камни и снимки останутся на месте.</p>
      </div>

      <div className="scroll">
        <label className="field">
          <span>Компания</span>
          <input value={form.companyName} onChange={set('companyName')} placeholder="Liming Gems Co., Ltd" />
        </label>
        <label className="field">
          <span>Как напечатано на визитке</span>
          <input value={form.companyNameLocal} onChange={set('companyNameLocal')} placeholder="利明宝石" />
        </label>

        <div className="pair">
          <label className="field"><span>Стенд</span>
            <input value={form.booth} onChange={set('booth')} placeholder="3B-21" /></label>
          <label className="field"><span>Зал</span>
            <input value={form.hall} onChange={set('hall')} placeholder="Hall 2" /></label>
        </div>
        {/* В здании стенда нет — есть этаж и офис. Показываем обе пары:
            карточка одна на все места, а человек сам знает, где он. */}
        <div className="pair">
          <label className="field"><span>Этаж</span>
            <input value={form.floor} onChange={set('floor')} placeholder="12" /></label>
          <label className="field"><span>Офис</span>
            <input value={form.office} onChange={set('office')} placeholder="1205" /></label>
        </div>

        <label className="field">
          <span>Кто на стенде</span>
          <input value={form.contactPerson} onChange={set('contactPerson')} placeholder="Mr. Chen" />
        </label>
        <label className="field">
          <span>Должность</span>
          <input value={form.position} onChange={set('position')} placeholder="Sales manager" />
        </label>

        <div className="pair">
          <label className="field"><span>Город</span>
            <input value={form.city} onChange={set('city')} placeholder="Бангкок" /></label>
          <label className="field"><span>Страна</span>
            <input value={form.country} onChange={set('country')} placeholder="Таиланд" /></label>
        </div>

        <label className="field">
          <span>Адрес или офис</span>
          <input value={form.address} onChange={set('address')} placeholder="Silom Road 123" />
        </label>
        <label className="field">
          <span>Сайт</span>
          <input value={form.website} onChange={set('website')} placeholder="liminggems.com" />
        </label>

        <div className="head"><h3>Связь</h3><span>{contacts.length}</span></div>
        {contacts.length === 0 && (
          <p className="meta" style={{ marginTop: 0 }}>Пока ни одного канала. Добавьте кнопкой ниже.</p>
        )}
        {contacts.map((c, i) => (
          <div className="pair" key={c.id} style={{ gridTemplateColumns: '1fr auto', alignItems: 'end' }}>
            <label className="field">
              <span>{KINDS.find((k) => k.id === c.kind)?.cap ?? c.kind}</span>
              <input
                value={c.value}
                inputMode={c.kind === 'email' ? 'email' : 'tel'}
                onChange={(e) => setContacts((list) =>
                  list.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
              />
            </label>
            <button className="btn btn-secondary btn-sm" style={{ marginBottom: 14, flex: 'none' }}
                    onClick={() => {
                      setRemoved((r) => [...r, c.id]);
                      setContacts((list) => list.filter((_, j) => j !== i));
                    }}>
              Убрать
            </button>
          </div>
        ))}

        <div className="states" style={{ marginTop: 4 }}>
          {KINDS.map((k) => (
            <button key={k.id} className="state-btn" aria-pressed={adding === k.id}
                    onClick={() => {
                      setContacts((list) => [...list, { id: ulid(), kind: k.id, value: '' }]);
                      setAdding(k.id);
                    }}>
              ＋ {k.cap}
            </button>
          ))}
        </div>

        <label className="field" style={{ marginTop: 18 }}>
          <span>Заметка о стенде</span>
          <textarea value={form.notes} onChange={set('notes')} rows={3}
                    placeholder="Возит из Мозамбика, торгуется на объёме" />
        </label>
      </div>

      <div className="dock">
        <button className="btn btn-secondary" onClick={onBack}>Отмена</button>
        <button className="btn btn-primary" disabled={!name || saving} onClick={() => void save()}>
          {saving ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
